import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createCommentSAV,
  getComment,
  getComments,
  getUpdatedCommentNotifications,
} from "../../REDUX/actions/ticketSAV";
import MessageItem, {
  ACTION_TYPE,
} from "./NewTicketSav/ChatContainer/MessageItem";
import LoaderWrapper from "../../Utils/ViewWrapper/LoaderWrapper/LoaderWrapper";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { isDefined } from "../../Utils";
import API from "../../Api/Api";
import RichTextEditor from "../RichTextEditor";

const SavChatMessages = ({
  scrollableComment,
  isAdmin,
  notificationCount,
  setScrollableComment,
}) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const {
    ticketSAV,
    currentAdminComments,
    currentComments,
    currentCommentsLoading,
    currentAdminCommentsLoading,
    createCommentLoading,
  } = useSelector((state) => state.ticketSAVReducer);
  const { socket } = useSelector((state) => state.socketReducer);
  const [newMessage, setNewMessage] = useState({ text: "", attachments: [] });
  const [isEditing, setIsEditing] = useState(false);
  const comments = useMemo(() => {
    return {
      list: isAdmin ? currentAdminComments : currentComments,
      loading: isAdmin ? currentAdminCommentsLoading : currentCommentsLoading,
    };
  }, [
    currentAdminComments,
    currentCommentsLoading,
    currentAdminCommentsLoading,
    currentComments,
    isAdmin,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "attachments") {
      setNewMessage((prev) => ({
        ...prev,
        [name]:
          prev?.attachments?.length > 0
            ? [...prev?.attachments, ...value]
            : value,
      }));
    } else {
      setNewMessage((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = () => {
    if (
      newMessage?.text?.trim()?.length > 0 ||
      newMessage?.attachments?.length > 0
    ) {
      dispatch(
        createCommentSAV(
          newMessage,
          ticketSAV?.id,
          isAdmin,
          user?.id,
          () => {
            setNewMessage({ text: "", attachments: [] });
          },
          t
        )
      );
    }
  };

  const groupMessagesByDate = (messages) => {
    return messages?.reduce((acc, message) => {
      const createdAt = message?.attributes?.createdAt;
      const formattedDate = createdAt ? moment(createdAt).format("LL") : "-"; // Example: January 31, 2025

      if (!acc[formattedDate]) {
        acc[formattedDate] = [];
      }
      acc[formattedDate].push(message);

      return acc;
    }, {});
  };

  const groupedMessages = useMemo(
    () => groupMessagesByDate(comments.list),
    [comments.list]
  );

  const handleReadNotifications = useCallback(async () => {
    try {
      if (notificationCount > 0 && ticketSAV) {
        await API.post("api/notifications-read", {
          data: {
            ticket_sav: ticketSAV.id,
            type: isAdmin ? "admin_comments" : "comments",
          },
        });
        dispatch(getUpdatedCommentNotifications(ticketSAV.id));
      }
    } catch {}
  }, [dispatch, isAdmin, notificationCount, ticketSAV]);

  useEffect(() => {
    if (isDefined(ticketSAV?.id)) {
      dispatch(getComments(ticketSAV?.id, isAdmin));
      if (socket) {
        const roomName = `${isAdmin ? "admin" : "client"}-sav-${ticketSAV?.id}`;
        socket.emit("join-room", roomName);

        socket.on("new-message", (messageId) => {
          dispatch(getComment(messageId, { isAdmin }));
          dispatch(getUpdatedCommentNotifications(ticketSAV?.id));
        });
        socket.on("update-message", (messageId) => {
          dispatch(getComment(messageId, { isAdmin, update: true }));
        });
        socket.on("delete-message", (messageId) => {
          dispatch({
            type: isAdmin
              ? ACTION_TYPE.SAV_ADMIN.delete
              : ACTION_TYPE.SAV.delete,
            payload: messageId,
          });
          dispatch(getUpdatedCommentNotifications(ticketSAV?.id));
        });

        return () => {
          socket.off("new-message");
          socket.off("update-message");
          socket.off("delete-message");
          socket.emit("leave-room", roomName);
        };
      }
    }
  }, [dispatch, ticketSAV?.id, isAdmin, socket]);

  useEffect(() => {
    if (isAdmin) {
      if (isDefined(scrollableComment) && currentAdminComments?.length > 0) {
        document
          .getElementById(scrollableComment)
          ?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      if (isDefined(scrollableComment) && currentComments?.length > 0) {
        document
          .getElementById(scrollableComment)
          ?.scrollIntoView({ behavior: "smooth" });
        setScrollableComment();
      }
    }
  }, [scrollableComment, currentComments, currentAdminComments]);

  useEffect(() => {
    handleReadNotifications();
  }, [handleReadNotifications]);

  return (
    <LoaderWrapper
      loading={currentCommentsLoading || currentAdminCommentsLoading}
    >
      <div className="newSavComment d-flex flex-column justify-content-end position-relative max-w-600px w-100 mx-auto">
        <div className="d-flex px-4 flex-column overflow-auto flex-column-reverse flex-grow-1 pt-3 pb-5">
          {Object.entries(groupedMessages).map(([date, messages]) => (
            <Fragment key={date}>
              {messages.map((comment, index) => (
                <MessageItem
                  key={comment?.id}
                  index={index}
                  message={comment}
                  isOwner={+user?.id === +comment?.attributes?.user?.data?.id}
                  view={isAdmin ? "SAV_ADMIN" : "SAV"}
                  position={index === 0 ? "bottom" : "top"}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                />
              ))}
              <div className="text-center text-muted my-2 px-4 py-2 f11 bg-dimGrey br5 sBold d-flex fit-content mx-auto text-dark">
                {date}
              </div>
            </Fragment>
          ))}
        </div>
        <div className={"px-4"}>
          <RichTextEditor
            loading={createCommentLoading}
            handleChange={handleChange}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            handleSubmit={handleSubmit}
          />
        </div>
      </div>
    </LoaderWrapper>
  );
};

export default SavChatMessages;
